import {Component, inject, input, OnChanges, OnInit, output} from '@angular/core'
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors} from '@angular/forms'
import {MatIconButton} from '@angular/material/button'
import {MatOption} from '@angular/material/core'
import {MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {MatSelect} from '@angular/material/select'
import {Property, PropertyType} from '@sparbanken-syd/kalpylator'
import {FormatNumberDirective} from '@sparbanken-syd/sparbanken-syd-theme'
import {noop, startWith} from 'rxjs'
import {BorgoService} from '../../../services/borgo.service'
import {KalpService} from '../../../services/kalp.service'
import {getProperty} from '../../../services/types'
import {PropertyTypeName, propertyTypeNames, propertyTypeToName} from '../application-types'

@Component({
  selector: 'spb-property',
  templateUrl: './property.component.html',
  styleUrls: ['./property.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatSuffix,
    MatIconButton,
    MatIcon,
    FormatNumberDirective
  ]
})
export class PropertyComponent implements OnInit, OnChanges {

  readonly propertyTypes = input<PropertyTypeName[]>(propertyTypeNames)

  readonly property = input<Property>(getProperty())

  /**
   * Special rules apply for the main property, titles and stuff.
   */
  readonly primaryProperty = input(false)

  /**
   * Used only to be able to remove
   */
  readonly index = input(0)

  /**
   * A method to remove a property
   */
  readonly removeProperty = input<((index: number) => void)>(noop)

  public valid = output<boolean>()

  public form = new FormGroup({
    propertyType: new FormControl<PropertyType>('VILLA', {nonNullable: true}),
    rent: new FormControl<number | string>('', {nonNullable: true}),
    runCost: new FormControl<number | string>('', {nonNullable: true})
  })

  public hasRent = false

  public propertyType: PropertyType = 'VILLA'

  private readonly rentals: PropertyType[] = ['BOSTADSRATT', 'HYRES']

  private readonly kalpService = inject(KalpService)
  private readonly borgoService = inject(BorgoService)


  constructor() {
    this.form.controls.rent.addValidators([this.rentValidator])
  }

  public ngOnInit(): void {
    this.form.controls.propertyType.valueChanges.pipe(
    ).subscribe((propertyType: PropertyType) => {
      this.property().propertyType = propertyType
      this.setDefaults()
    })

    this.form.valueChanges.pipe(
      startWith({})
    ).subscribe(() => {
      const property = this.property()
      Object.assign(property, this.form.getRawValue())
      if (property.primary) {
        property.friendlyName = 'Belåningsobjekt'
      } else {
        property.id = this.index() + 'x'
        property.friendlyName =
          `Övrigt boende (${propertyTypeToName(property.propertyType)})`
      }
    })

    this.form.statusChanges.pipe(startWith('INVALID'))
      .subscribe(() => {
        this.valid.emit(this.form.status === 'VALID')
      })
  }

  public ngOnChanges() {
    this.form.patchValue(this.property())
    this.form.controls.propertyType.updateValueAndValidity()
  }

  private readonly rentValidator = (control: AbstractControl): ValidationErrors | null => {
    if (this.hasRent && (control.value === '' || control.value === 0)) {
      return {
        rent: 'required'
      }
    }
    return null
  }

  private setDefaults(): void {
    const borgoOrSparbanken = this.borgoService.domain()
    const property = this.property()
    this.kalpService.propertyDefaults(property!, borgoOrSparbanken)
    this.form.controls.runCost.setValue(property!.runCost as number, {emitEvent: false})
    this.hasRent = this.rentals.includes(property!.propertyType)
    this.propertyType = property!.propertyType
    this.form.controls.rent.setValue(property!.rent as number)
  }

}
